import React from 'react';
import { Search, ShieldCheck, BarChart3, FileSearch } from 'lucide-react';
import CodeBlock from '../components/CodeBlock';

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      className="font-heading text-2xl font-semibold mt-16 mb-4 scroll-mt-20"
      style={{ color: 'var(--text-primary)' }}
    >
      {children}
    </h2>
  );
}

function Paragraph({ children }) {
  return (
    <p className="leading-relaxed mb-4 font-body" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </p>
  );
}

function EndpointHeader({ method, path }) {
  const badge = `badge-${method.toLowerCase()}`;
  return (
    <div className="flex items-center gap-3 mb-4 mt-8">
      <span className={badge}>{method}</span>
      <code className="font-mono" style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>{path}</code>
    </div>
  );
}

function ParamTable({ params }) {
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td className="font-mono text-xs" style={{ color: 'var(--accent-primary)', opacity: 0.8 }}>{p.name}</td>
              <td className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{p.type}</td>
              <td>
                {p.required ? (
                  <span className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>Yes</span>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No</span>
                )}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusCodes({ codes }) {
  const colorMap = {
    '200': 'var(--accent-success)', '201': 'var(--accent-success)', '202': 'var(--accent-success)',
    '204': 'var(--accent-success)', '400': 'var(--accent-warning)', '401': 'var(--accent-danger)',
    '403': 'var(--accent-danger)', '404': 'var(--accent-warning)', '409': 'var(--accent-warning)',
    '422': 'var(--accent-warning)', '429': 'var(--accent-warning)', '500': 'var(--accent-danger)',
  };
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr><th>Status</th><th>Description</th></tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code}>
              <td
                className="font-mono text-xs font-semibold"
                style={{ color: colorMap[c.code] || 'var(--text-secondary)' }}
              >
                {c.code}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AGENT_TYPES = [
  {
    type: 'analyzer',
    desc: 'Image/video forensic analysis',
    Icon: FileSearch,
  },
  {
    type: 'detector',
    desc: 'Deepfake and tampering detection',
    Icon: Search,
  },
  {
    type: 'verifier',
    desc: 'Cross-reference and provenance',
    Icon: ShieldCheck,
  },
  {
    type: 'reporter',
    desc: 'Compliance report generation',
    Icon: BarChart3,
  },
];

export default function AgentAPI() {
  return (
    <article>
      <div className="mb-12">
        <p
          className="text-xs font-mono uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-primary)', opacity: 0.6 }}
        >
          Agent API
        </p>
        <h1
          className="font-heading font-bold mb-4"
          style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          Agent Orchestration
        </h1>
        <Paragraph>
          Register, execute, and monitor AI agents for evidence analysis. Agents
          run in sandboxed environments with constrained permissions and produce
          auditable results that are automatically anchored to the WORM chain.
        </Paragraph>

        {/* Agent type cards */}
        <div className="grid grid-cols-2 gap-3 my-6">
          {AGENT_TYPES.map((a) => (
            <div key={a.type} className="glass-card p-4 flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(0,212,255,0.1)' }}
              >
                <a.Icon className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <div>
                <code className="font-mono text-xs" style={{ color: 'var(--accent-primary)', opacity: 0.8 }}>
                  {a.type}
                </code>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Register ── */}
      <SectionHeading id="register">Register Agent</SectionHeading>
      <Paragraph>
        Register a new agent with defined capabilities, resource limits, and
        security constraints. Each agent receives a unique ID and API credentials
        for execution.
      </Paragraph>

      <EndpointHeader method="POST" path="/api/v1/agents" />

      <ParamTable
        params={[
          { name: 'name', type: 'string', required: true, description: 'Human-readable agent name.' },
          { name: 'type', type: 'string', required: true, description: 'Agent type: analyzer, detector, verifier, reporter.' },
          { name: 'description', type: 'string', required: false, description: 'Description of the agent purpose and capabilities.' },
          { name: 'capabilities', type: 'string[]', required: true, description: 'List of capabilities: image_analysis, video_analysis, deepfake_detection, metadata_extraction, hash_verification, report_generation.' },
          { name: 'model', type: 'string', required: false, description: 'AI model identifier (e.g., "noyka-forensic-v3").' },
          { name: 'max_concurrent', type: 'integer', required: false, description: 'Max concurrent executions (default: 5).' },
          { name: 'timeout_seconds', type: 'integer', required: false, description: 'Execution timeout in seconds (default: 300, max: 3600).' },
          { name: 'sandbox_policy', type: 'string', required: false, description: 'Sandbox level: strict, standard, permissive (default: strict).' },
        ]}
      />

      {/* Sandbox config info */}
      <div className="glass-card p-5 my-4">
        <h4 className="font-heading font-medium text-sm mb-3" style={{ color: 'var(--text-primary)' }}>
          Sandbox Configuration
        </h4>
        <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <div className="flex items-start gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,229,160,0.1)', color: 'var(--accent-success)' }}>strict</span>
            <span>No network access, read-only filesystem, 4 CPU cores, 8GB RAM max</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,184,0,0.1)', color: 'var(--accent-warning)' }}>standard</span>
            <span>Allowlisted network endpoints, temp filesystem, 8 CPU cores, 16GB RAM max</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,45,85,0.1)', color: 'var(--accent-danger)' }}>permissive</span>
            <span>Full network, read-write filesystem, 16 CPU cores, 32GB RAM max (requires admin)</span>
          </div>
        </div>
      </div>

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl -X POST https://api.noyka.ai/api/v1/agents \\
  -H "Authorization: Bearer eyJhbGciOi..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Forensic Image Analyzer",
    "type": "analyzer",
    "description": "Deep forensic analysis of image evidence including ELA, PRNU, and metadata verification",
    "capabilities": [
      "image_analysis",
      "metadata_extraction",
      "deepfake_detection"
    ],
    "model": "noyka-forensic-v3",
    "max_concurrent": 10,
    "timeout_seconds": 600,
    "sandbox_policy": "strict"
  }'`,
          python: `response = requests.post(
    "https://api.noyka.ai/api/v1/agents",
    headers={
        "Authorization": "Bearer eyJhbGciOi...",
        "Content-Type": "application/json",
    },
    json={
        "name": "Forensic Image Analyzer",
        "type": "analyzer",
        "description": "Deep forensic analysis of image evidence",
        "capabilities": [
            "image_analysis",
            "metadata_extraction",
            "deepfake_detection",
        ],
        "model": "noyka-forensic-v3",
        "max_concurrent": 10,
        "timeout_seconds": 600,
        "sandbox_policy": "strict",
    },
)
agent = response.json()`,
          javascript: `const response = await fetch("https://api.noyka.ai/api/v1/agents", {
  method: "POST",
  headers: {
    Authorization: "Bearer eyJhbGciOi...",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    name: "Forensic Image Analyzer",
    type: "analyzer",
    description: "Deep forensic analysis of image evidence",
    capabilities: [
      "image_analysis",
      "metadata_extraction",
      "deepfake_detection",
    ],
    model: "noyka-forensic-v3",
    max_concurrent: 10,
    timeout_seconds: 600,
    sandbox_policy: "strict",
  }),
});
const agent = await response.json();`,
          go: `payload := map[string]interface{}{
    "name":            "Forensic Image Analyzer",
    "type":            "analyzer",
    "description":     "Deep forensic analysis of image evidence",
    "capabilities":    []string{"image_analysis", "metadata_extraction", "deepfake_detection"},
    "model":           "noyka-forensic-v3",
    "max_concurrent":  10,
    "timeout_seconds": 600,
    "sandbox_policy":  "strict",
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", "https://api.noyka.ai/api/v1/agents",
    bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
req.Header.Set("Content-Type", "application/json")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 201"
        examples={{
          json: `{
  "id": "agt_4a5b6c7d8e9f0123",
  "name": "Forensic Image Analyzer",
  "type": "analyzer",
  "description": "Deep forensic analysis of image evidence including ELA, PRNU, and metadata verification",
  "capabilities": [
    "image_analysis",
    "metadata_extraction",
    "deepfake_detection"
  ],
  "model": "noyka-forensic-v3",
  "status": "registered",
  "credentials": {
    "agent_key": "nyk_agent_x1y2z3w4v5u6...",
    "agent_secret": "nyk_asec_••••••••"
  },
  "config": {
    "max_concurrent": 10,
    "timeout_seconds": 600,
    "sandbox_policy": "strict"
  },
  "created_at": "2026-03-13T10:00:00Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '201', description: 'Agent registered successfully.' },
          { code: '400', description: 'Invalid request body or unsupported agent type.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Referenced model not found.' },
        ]}
      />

      {/* ── Execute ── */}
      <SectionHeading id="execute">Execute Agent</SectionHeading>
      <Paragraph>
        Trigger an agent execution against one or more pieces of evidence. The
        execution runs asynchronously in a sandboxed environment. Use the monitor
        endpoint to track progress and retrieve results.
      </Paragraph>

      <EndpointHeader method="POST" path="/api/v1/agents/{id}/execute" />

      <ParamTable
        params={[
          { name: 'id', type: 'string', required: true, description: 'Agent ID (path parameter).' },
          { name: 'evidence_ids', type: 'string[]', required: true, description: 'Array of evidence IDs to analyze.' },
          { name: 'parameters', type: 'object', required: false, description: 'Agent-specific execution parameters.' },
          { name: 'priority', type: 'string', required: false, description: 'Execution priority: low, normal, high, critical (default: normal).' },
          { name: 'callback_url', type: 'string', required: false, description: 'Webhook URL for completion notification.' },
          { name: 'idempotency_key', type: 'string', required: false, description: 'Unique key to prevent duplicate executions.' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl -X POST https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/execute \\
  -H "Authorization: Bearer eyJhbGciOi..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: exec_unique_001" \\
  -d '{
    "evidence_ids": [
      "ev_8f3a2b1c4d5e6789",
      "ev_2c3d4e5f6a7b8901"
    ],
    "parameters": {
      "analysis_depth": "comprehensive",
      "detect_manipulation": true,
      "extract_faces": true,
      "compare_with_known": true
    },
    "priority": "high",
    "callback_url": "https://yourapp.com/webhooks/agent-complete"
  }'`,
          python: `response = requests.post(
    "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/execute",
    headers={
        "Authorization": "Bearer eyJhbGciOi...",
        "Content-Type": "application/json",
        "Idempotency-Key": "exec_unique_001",
    },
    json={
        "evidence_ids": [
            "ev_8f3a2b1c4d5e6789",
            "ev_2c3d4e5f6a7b8901",
        ],
        "parameters": {
            "analysis_depth": "comprehensive",
            "detect_manipulation": True,
            "extract_faces": True,
            "compare_with_known": True,
        },
        "priority": "high",
        "callback_url": "https://yourapp.com/webhooks/agent-complete",
    },
)
execution = response.json()`,
          javascript: `const response = await fetch(
  "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/execute",
  {
    method: "POST",
    headers: {
      Authorization: "Bearer eyJhbGciOi...",
      "Content-Type": "application/json",
      "Idempotency-Key": "exec_unique_001",
    },
    body: JSON.stringify({
      evidence_ids: [
        "ev_8f3a2b1c4d5e6789",
        "ev_2c3d4e5f6a7b8901",
      ],
      parameters: {
        analysis_depth: "comprehensive",
        detect_manipulation: true,
        extract_faces: true,
        compare_with_known: true,
      },
      priority: "high",
      callback_url: "https://yourapp.com/webhooks/agent-complete",
    }),
  }
);
const execution = await response.json();`,
          go: `payload := map[string]interface{}{
    "evidence_ids": []string{
        "ev_8f3a2b1c4d5e6789",
        "ev_2c3d4e5f6a7b8901",
    },
    "parameters": map[string]interface{}{
        "analysis_depth":     "comprehensive",
        "detect_manipulation": true,
        "extract_faces":      true,
        "compare_with_known": true,
    },
    "priority":     "high",
    "callback_url": "https://yourapp.com/webhooks/agent-complete",
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST",
    "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/execute",
    bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
req.Header.Set("Content-Type", "application/json")
req.Header.Set("Idempotency-Key", "exec_unique_001")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 201"
        examples={{
          json: `{
  "execution_id": "exec_7f8e9d0c1b2a3456",
  "agent_id": "agt_4a5b6c7d8e9f0123",
  "status": "queued",
  "evidence_ids": [
    "ev_8f3a2b1c4d5e6789",
    "ev_2c3d4e5f6a7b8901"
  ],
  "priority": "high",
  "estimated_duration_seconds": 120,
  "progress": {
    "percentage": 0,
    "current_step": "initializing",
    "total_steps": 6
  },
  "sandbox": {
    "id": "sbx_a1b2c3d4",
    "policy": "strict",
    "resource_limits": {
      "cpu_cores": 4,
      "memory_mb": 8192,
      "gpu": true
    }
  },
  "created_at": "2026-03-13T10:35:00Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '201', description: 'Execution queued successfully.' },
          { code: '400', description: 'Invalid request body or evidence IDs.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Agent not found.' },
        ]}
      />

      {/* ── Monitor ── */}
      <SectionHeading id="monitor">Monitor Execution</SectionHeading>
      <Paragraph>
        Check the status and results of an agent execution. Once completed,
        the response includes full analysis results, confidence scores, and
        any detected findings.
      </Paragraph>

      <EndpointHeader method="GET" path="/api/v1/agents/{id}/status" />

      <ParamTable
        params={[
          { name: 'id', type: 'string', required: true, description: 'Agent ID (path parameter).' },
          { name: 'execution_id', type: 'string', required: false, description: 'Specific execution ID. If omitted, returns latest execution.' },
          { name: 'include_results', type: 'boolean', required: false, description: 'Include full results in response (default: true for completed executions).' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/status?execution_id=exec_7f8e9d0c1b2a3456" \\
  -H "Authorization: Bearer eyJhbGciOi..."`,
          python: `response = requests.get(
    "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/status",
    headers={"Authorization": "Bearer eyJhbGciOi..."},
    params={"execution_id": "exec_7f8e9d0c1b2a3456"},
)
status = response.json()`,
          javascript: `const response = await fetch(
  "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/status?execution_id=exec_7f8e9d0c1b2a3456",
  { headers: { Authorization: "Bearer eyJhbGciOi..." } }
);
const status = await response.json();`,
          go: `url := "https://api.noyka.ai/api/v1/agents/agt_4a5b6c7d8e9f0123/status"
req, _ := http.NewRequest("GET", url, nil)
q := req.URL.Query()
q.Add("execution_id", "exec_7f8e9d0c1b2a3456")
req.URL.RawQuery = q.Encode()
req.Header.Set("Authorization", "Bearer eyJhbGciOi...")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      <CodeBlock
        title="Response 200 (completed)"
        examples={{
          json: `{
  "execution_id": "exec_7f8e9d0c1b2a3456",
  "agent_id": "agt_4a5b6c7d8e9f0123",
  "status": "completed",
  "progress": {
    "percentage": 100,
    "current_step": "finalized",
    "total_steps": 6
  },
  "duration_seconds": 87,
  "results": {
    "summary": "Analysis of 2 evidence items completed. 1 anomaly detected.",
    "evidence_results": [
      {
        "evidence_id": "ev_8f3a2b1c4d5e6789",
        "confidence_score": 0.94,
        "findings": [
          {
            "type": "metadata_inconsistency",
            "severity": "medium",
            "description": "EXIF creation date does not match filesystem timestamp",
            "details": {
              "exif_date": "2026-03-10T08:30:00Z",
              "fs_date": "2026-03-12T14:22:00Z",
              "delta_hours": 53.87
            }
          }
        ],
        "analysis": {
          "ela_score": 0.12,
          "prnu_match": true,
          "deepfake_probability": 0.03,
          "manipulation_detected": false
        }
      },
      {
        "evidence_id": "ev_2c3d4e5f6a7b8901",
        "confidence_score": 0.98,
        "findings": [],
        "analysis": {
          "ela_score": 0.05,
          "prnu_match": true,
          "deepfake_probability": 0.01,
          "manipulation_detected": false
        }
      }
    ],
    "worm_record_id": "worm_f1e2d3c4b5a69870"
  },
  "started_at": "2026-03-13T10:35:02Z",
  "completed_at": "2026-03-13T10:36:29Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Execution status retrieved successfully.' },
          { code: '400', description: 'Invalid query parameters.' },
          { code: '401', description: 'Invalid or expired authentication token.' },
          { code: '404', description: 'Agent or execution not found.' },
        ]}
      />
    </article>
  );
}
